import {
  ClinicianDTO,
  PatientWithAccessRightDTO,
  UpdatePatientDTO,
} from '@peartherapeutics/weaver-api-client';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ErrorInterceptor from 'components/common/ErrorInterceptor';
import { hasRequiredScope, scopes } from 'utils/scope';
import Program from 'utils/program';
import { programs } from 'utils/constants';
import PromiseBluebird from 'bluebird';
import AuthorizationComponent from 'components/common/AuthorizationComponent';
import { WeaverApiClientInstance } from 'api/weaverApiClientInstance';
import {
  KakarikoEditingCell,
  KakarikoGrid,
  KakarikoGridHeader,
  KakarikoGridHeaderCell,
  KakarikoGridRow,
  KakarikoSelect,
  KakarikoText,
  Validations,
} from 'components/library';
import AddPatient from './AddPatient';
import './styles.scss';

interface IOwnProps {
  currentProgram: string;
}

interface IStateProps {
  clinicId: string;
  clinicName: string;
  currentUserRoles: string[];
  currentUserScopes: string[];
}

type ResetManagePatientsType = IStateProps & IOwnProps;

export const HeaderLabels = {
  lastName: 'Last Name',
  firstName: 'First Name',
  dateOfBirth: 'Date of Birth',
  email: 'Email',
  healthCareProvider: 'Health Care Provider',
  treatmentArm: 'Treatment Arm',
  externalPatientId: 'External Patient ID',
  primaryRef: 'Pear Primary ID',
  actions: 'Actions',
};

export const ManagePatients = (props: ResetManagePatientsType) => {
  const [promiseAPIs, setPromiseAPIs] = useState<{
    patientsPromise: Nullable<PromiseBluebird<any>>;
    cliniciansPromise: Nullable<PromiseBluebird<any>>;
  }>({
    patientsPromise: null,
    cliniciansPromise: null,
  });

  const [isInvoking, setIsInvoking] = useState<{
    patientsPromise: boolean;
    cliniciansPromise: boolean;
  }>({
    patientsPromise: false,
    cliniciansPromise: false,
  });

  const [clinicians, setClinicians] = useState<ClinicianDTO[]>([]);
  const [featureGroups, setFeatureGroups] = useState<FeatureGroupDTO[]>([]);
  const [giftCardVendors, setGiftCardVendors] = useState<GiftCardVendorDTO[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingPatient, setIsLoadingPatient] = useState<boolean>(false);
  const [isLoadingClinician, setIsLoadingClinician] = useState<boolean>(false);
  const [patients, setPatients] = useState<PatientWithAccessRightDTO[]>([]);

  const { clinicId, clinicName, currentProgram, currentUserScopes } = props;

  const [prevProps, setPrevProps] = useState<{
    currentProgram: string;
    clinicId: string;
  }>({
    currentProgram,
    clinicId,
  });

  const hasScope = hasRequiredScope(currentUserScopes);

  const getGiftCardVendors = useCallback(async () => {
    try {
      if (hasScope(scopes.ADD_PATIENT)) {
        const { data: giftCardVendors } =
          await WeaverApiClientInstance.getGiftCardVendors();
        setGiftCardVendors(giftCardVendors);
      }
    } catch (e) {
      ErrorInterceptor.error(e);
    }
  }, [hasScope]);

  const getPatientsByClinicPromise = useCallback(
    (clinicId: string) => {
      if (isInvoking.patientsPromise && promiseAPIs.patientsPromise) {
        promiseAPIs.patientsPromise.cancel();
      }

      return new Promise<void>((resolve, reject) => {
        const patientsPromise = new PromiseBluebird(
          async (resolveBluebird, rejectBluebird, onCancel) => {
            try {
              setIsLoadingPatient(true);
              setIsInvoking((prevState) => ({
                ...prevState,
                patientsPromise: true,
              }));
              const program: string = Program.get().name;
              const patients = await WeaverApiClientInstance.getPatients(
                clinicId,
                program,
              );
              if (clinicName) {
                resolveBluebird(patients);
              } else {
                resolveBluebird(null);
              }

              // @ts-ignore
              onCancel(() => {
                resolveBluebird(null);
                setIsInvoking((prevState) => ({
                  ...prevState,
                  patientsPromise: false,
                }));
              });
            } catch (error) {
              rejectBluebird(error);
            }
          },
        )
          .then((result: any) => {
            if (result !== null) {
              setPatients(result);

              resolve();
            }
          })
          .catch((error) => {
            reject(error);
          })
          .finally(() => {
            setIsLoadingPatient(false);
          });
        setPromiseAPIs((prevState) => ({
          ...prevState,
          patientsPromise,
        }));
      });
    },
    [clinicName, isInvoking.patientsPromise, promiseAPIs.patientsPromise],
  );

  const getCliniciansByClinicPromise = useCallback(
    (clinicId) => {
      if (isInvoking.cliniciansPromise && promiseAPIs.cliniciansPromise) {
        promiseAPIs.cliniciansPromise.cancel();
      }
      return new Promise<void>((resolve, reject) => {
        const cliniciansPromise = new PromiseBluebird(
          async (resolveBluebird, rejectBluebird, onCancel) => {
            try {
              setIsLoadingClinician(true);
              setIsInvoking((prevState) => ({
                ...prevState,
                cliniciansPromise: true,
              }));
              const { data: clinicians } =
                await WeaverApiClientInstance.getAllActiveCliniciansForLocation(
                  clinicId,
                );
              resolveBluebird(clinicians);
              // @ts-ignore
              onCancel(() => {
                resolveBluebird(null);
                setIsInvoking((prevState) => ({
                  ...prevState,
                  cliniciansPromise: false,
                }));
              });
            } catch (error) {
              rejectBluebird(error);
            }
          },
        )
          .then((result: any) => {
            if (result !== null) {
              setClinicians(result);

              resolve();
            }
          })
          .catch((error) => {
            reject(error);
          })
          .finally(() => {
            setIsLoadingClinician(false);
          });

        setPromiseAPIs((prevState) => ({
          ...prevState,
          cliniciansPromise,
        }));
      });
    },
    [isInvoking.cliniciansPromise, promiseAPIs.cliniciansPromise],
  );

  const getPatientAndClinicianList = useCallback(
    async (clinicId: string) => {
      try {
        if (clinicId !== '' && Program.get()) {
          await Promise.all([
            getPatientsByClinicPromise(clinicId),
            getCliniciansByClinicPromise(clinicId),
          ]);
        } else {
          setPatients([]);
          setClinicians([]);
        }
      } catch (e) {
        ErrorInterceptor.error(e);
      }
    },
    [getCliniciansByClinicPromise, getPatientsByClinicPromise],
  );

  const reloadPatientsAndCliniciansList = async (clinicId: string) => {
    setPatients([]);
    setClinicians([]);
    getPatientAndClinicianList(clinicId);
  };

  const updatePatient = (patientData: PatientWithAccessRightDTO) => {
    const oldDataIndex: number = patients.findIndex(
      (p) => p.id === patientData.id,
    );
    const updatedPatients = [...patients];
    if (oldDataIndex !== -1) {
      updatedPatients.splice(oldDataIndex, 1, patientData);
    }
    setPatients(updatedPatients);
  };

  const getFeatureGroup = useCallback(async () => {
    const isPear002aProgram = currentProgram === programs.PEAR002A;
    let featureGroups: FeatureGroupDTO[] = [];

    if (isPear002aProgram) {
      const { data = [] } = await WeaverApiClientInstance.getAllFeatureGroups();
      featureGroups = data.map((item: FeatureGroupDTO) => ({
        id: item.id,
        name: item.name,
        rxProviderId: item.rxProviderId,
      }));
    }
    setFeatureGroups(featureGroups);
  }, [currentProgram]);

  const handleUpdatePatient = async (patient: UpdatePatientDTO) => {
    try {
      const { data } = await WeaverApiClientInstance.updatePatient({
        locationId: clinicId,
        id: patient.id,
        firstName: patient.firstName,
        lastName: patient.lastName,
        birthDate: patient.birthDate as string,
        healthCareProviderId: patient.healthCareProviderId ?? '',
        externalRef: patient.externalRef ?? '',
        email: patient.email?.trim(),
        userId: patient.userId,
      });

      updatePatient(data);
      return true;
    } catch (e) {
      ErrorInterceptor.error(e.response.data);
      return false;
    }
  };

  useEffect(() => {
    getGiftCardVendors();
    reloadPatientsAndCliniciansList(clinicId);
    getFeatureGroup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (prevProps.currentProgram !== currentProgram) {
      setPrevProps((prevState) => ({
        ...prevState,
        currentProgram,
      }));
      getFeatureGroup();
    }

    if (prevProps.clinicId !== clinicId) {
      setPrevProps((prevState) => ({
        ...prevState,
        clinicId,
      }));

      getPatientAndClinicianList(clinicId);
    }
  }, [
    clinicId,
    currentProgram,
    getFeatureGroup,
    getPatientAndClinicianList,
    prevProps,
  ]);

  const displayClinicianName = useCallback(
    (option: string) => {
      const clinicianData = clinicians.find(
        (clinician) => clinician.id === option,
      )!;
      return `${clinicianData?.firstName || ''} ${
        clinicianData?.lastName || ''
      }`;
    },
    [clinicians],
  );

  const displayFeatureGroupName = useCallback(
    (option: string) => {
      const featureGroupdata = featureGroups.find(
        (featureGroup) => featureGroup.id === option,
      )!;
      return featureGroupdata?.name;
    },
    [featureGroups],
  );

  const gridHeaders = useMemo(
    () => (
      <KakarikoGridHeader>
        <KakarikoGridHeaderCell fractionWidth={2} sortName={'lastName'}>
          Last Name
        </KakarikoGridHeaderCell>

        <KakarikoGridHeaderCell fractionWidth={2} sortName={'firstName'}>
          First Name
        </KakarikoGridHeaderCell>

        <KakarikoGridHeaderCell fractionWidth={2} sortName={'dateOfBirth'}>
          Date of Birth
        </KakarikoGridHeaderCell>

        <KakarikoGridHeaderCell fractionWidth={3} sortName={'email'}>
          Email
        </KakarikoGridHeaderCell>

        <KakarikoGridHeaderCell
          fractionWidth={2}
          sortName={'heathCareProvider'}
        >
          Health Care Provider
        </KakarikoGridHeaderCell>

        {currentProgram === programs.PEAR002A && (
          <KakarikoGridHeaderCell fractionWidth={2} sortName={'treatmentArm'}>
            Treatment Arm
          </KakarikoGridHeaderCell>
        )}

        <KakarikoGridHeaderCell
          fractionWidth={2}
          sortName={'externalPatientId'}
        >
          External Patient ID
        </KakarikoGridHeaderCell>

        <KakarikoGridHeaderCell fractionWidth={2} sortName={'primaryRef'}>
          Pear Primary ID
        </KakarikoGridHeaderCell>

        <AuthorizationComponent requiredScope={scopes.UPDATE_PATIENT}>
          <KakarikoGridHeaderCell fractionWidth={2}>
            Actions
          </KakarikoGridHeaderCell>
        </AuthorizationComponent>
      </KakarikoGridHeader>
    ),
    [currentProgram],
  );

  const rowTemplate = (patient: PatientWithAccessRightDTO) => {
    return (
      <KakarikoGridRow data={patient} handleDataUpdate={handleUpdatePatient}>
        <KakarikoText name={'lastName'} validation={[Validations.isNotEmpty]} />
        <KakarikoText
          name={'firstName'}
          validation={[Validations.isNotEmpty]}
        />
        <KakarikoText
          name={'birthDate'}
          validation={[Validations.isNotEmpty]}
        />
        <KakarikoText name={'email'} validation={[Validations.isNotEmpty]} />
        <KakarikoSelect
          name={'primaryHealthCareProviderId'}
          options={clinicians?.map((clinician) => clinician.id)}
          displayValue={displayClinicianName}
          validation={[Validations.isNotEmpty]}
        />
        {currentProgram === programs.PEAR002A && (
          <KakarikoSelect
            name={'featureGroupId'}
            options={featureGroups?.map((featureGroup) => featureGroup.id)}
            displayValue={displayFeatureGroupName}
            validation={[Validations.isNotEmpty]}
          />
        )}
        <KakarikoText
          name={'externalRef'}
          placeholder="External Patient ID"
          value={patient.externalRef || ''}
        />

        <KakarikoText name={'id'} isEditable={false} />

        <AuthorizationComponent requiredScope={scopes.UPDATE_PATIENT}>
          <KakarikoEditingCell />
        </AuthorizationComponent>
      </KakarikoGridRow>
    );
  };

  useEffect(() => {
    setIsLoading(isLoadingPatient || isLoadingClinician);
  }, [isLoadingPatient, isLoadingClinician]);

  return (
    <div
      styleName="patient-table-wrapper"
      data-testid={
        isLoading
          ? 'manage-patients-reset-loaded'
          : 'manage-patients-reset-loading'
      }
    >
      <AuthorizationComponent requiredScope={scopes.ADD_PATIENT}>
        <AddPatient
          clinicName={clinicName}
          clinicId={clinicId}
          onCreateSuccess={() => getPatientAndClinicianList(clinicId)}
          clinicians={clinicians}
          vendors={giftCardVendors}
          featureGroups={featureGroups}
        />
      </AuthorizationComponent>
      <KakarikoGrid
        data={patients}
        header={gridHeaders}
        isLoading={isLoading}
        rowTemplate={rowTemplate}
        hasStickyHeader
        tableTheme={'grey'}
      />
    </div>
  );
};

export default ManagePatients;
